import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_BASE_URL}/order/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStats(response.data);
      } catch (err) {
        console.error("Error fetching dashboard stats:", err);
        setError("Couldn't load dashboard stats. Try refreshing.");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  // The fetch can fail entirely (network error, expired session, server
  // down) - when it does, `stats` stays null forever, `loading` still
  // turns false, and every card below reads a field off of it. Bail out
  // to a plain error state instead of letting that crash the page.
  if (!loading && !stats) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-ink mb-6">Dashboard</h1>
        <div className="px-4 py-3 rounded bg-primary/10 text-primary text-sm max-w-md">
          {error || "Couldn't load dashboard stats."}
        </div>
      </div>
    );
  }

  // Individual fields default to safe values too - the stats endpoint
  // itself already guards its aggregations (see server: GET /order/stats),
  // but a field being unexpectedly missing should render as "0", never
  // throw or print "undefined" to the screen.
  const ordersToday = stats?.ordersToday ?? 0;
  const ordersTotal = stats?.ordersTotal ?? 0;
  const revenueToday = stats?.revenueToday ?? 0;
  const revenueTodayCoverage = stats?.revenueTodayCoverage ?? 0;
  const revenueTotal = stats?.revenueTotal ?? 0;
  const revenueTotalCoverage = stats?.revenueTotalCoverage ?? 0;
  const recentOrders = stats?.recentOrders ?? [];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-ink mb-6">Dashboard</h1>

      {error && (
        <div className="mb-4 px-4 py-3 rounded bg-primary/10 text-primary text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-card border border-line rounded-lg p-5">
          <p className="text-xs uppercase tracking-wide text-muted font-semibold">
            Orders today
          </p>
          <p className="text-3xl font-bold text-ink mt-1">
            {loading ? "…" : ordersToday}
          </p>
        </div>
        <div className="bg-card border border-line rounded-lg p-5">
          <p className="text-xs uppercase tracking-wide text-muted font-semibold">
            Orders on record
          </p>
          <p className="text-3xl font-bold text-ink mt-1">
            {loading ? "…" : ordersTotal}
          </p>
        </div>
        <div className="bg-card border border-line rounded-lg p-5">
          <p className="text-xs uppercase tracking-wide text-muted font-semibold">
            Revenue today
          </p>
          <p className="text-3xl font-bold text-ink mt-1">
            {loading ? "…" : `৳${revenueToday}`}
          </p>
          {!loading && revenueTodayCoverage < ordersToday && (
            <p className="text-[11px] text-muted mt-1">
              {ordersToday - revenueTodayCoverage} order(s) today have no recorded total
            </p>
          )}
        </div>
        <div className="bg-card border border-line rounded-lg p-5">
          <p className="text-xs uppercase tracking-wide text-muted font-semibold">
            Revenue on record
          </p>
          <p className="text-3xl font-bold text-ink mt-1">
            {loading ? "…" : `৳${revenueTotal}`}
          </p>
          {!loading && revenueTotalCoverage < ordersTotal && (
            <p className="text-[11px] text-muted mt-1">
              Only counts orders placed since totals started being recorded
              ({revenueTotalCoverage} of {ordersTotal})
            </p>
          )}
        </div>
      </div>

      <div className="bg-card border border-line rounded-lg p-5">
        <p className="text-sm font-semibold text-ink mb-3">Recent orders</p>
        {loading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : recentOrders.length === 0 ? (
          <p className="text-sm text-muted">No orders yet.</p>
        ) : (
          <div className="divide-y divide-line">
            {recentOrders.map((order) => (
              <div
                key={order._id}
                className="flex items-center justify-between py-2.5 text-sm"
              >
                <div>
                  <span className="font-mono text-sub">#{order.orderId}</span>
                  <span className="text-ink ml-3">{order.name}</span>
                  <span className="text-xs text-muted ml-2 capitalize">
                    {(order.status || "pending").replace("_", " ")}
                  </span>
                  {order.notificationFailed && (
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                      notify failed
                    </span>
                  )}
                </div>
                <span className="text-muted text-xs">
                  {new Date(order.createdAt).toLocaleString("en-BD", {
                    timeZone: "Asia/Dhaka",
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
